import React from "react";
import {
  Bath,
  Bed,
  Grid,
  CalendarClock,
} from "@landvoice/icons";
import { Scaling, House } from "lucide-react";

import { Divider, InfoIcon } from "./PropertySummary";

import { Listing } from "src/types/listing";

type StatKey = "beds" | "bath" | "sqrFeet" | "lotSize" | "yearBuilt" | "type";

export interface ListingStatsProps {
  listing: Listing;
  show?: StatKey[]; // Optional: if omitted, show all
}

export const ListingStats = ({
  listing,
  show = ["beds", "bath", "sqrFeet", "lotSize"],
}: ListingStatsProps) => {
  const {
    beds,
    bath,
    formattedSqrFeet,
    lotSize,
    formattedLotSize,
    yearBuilt,
    type,
  } = listing;

  // Define all possible stats
  const allStats: { key: StatKey; element: React.ReactNode }[] = [
    {
      key: "beds",
      element: (
        <InfoIcon gap="8px" icon={<Bed />} text={`${beds ? beds : "--"} Bed`} />
      ),
    },
    {
      key: "bath",
      element: (
        <InfoIcon
          gap="8px"
          icon={<Bath />}
          text={`${bath ? bath : "--"} Bath`}
        />
      ),
    },
    {
      key: "sqrFeet",
      element: (
        <InfoIcon
          gap="8px"
          icon={<Grid />}
          text={`${formattedSqrFeet} Sq Ft`}
        />
      ),
    },
    {
      key: "lotSize",
      element: (
        <InfoIcon
          gap="8px"
          icon={<Scaling size={16} />}
          text={` LtSz ${lotSize ? formattedLotSize : "--"}`}
        />
      ),
    },
    {
      key: "yearBuilt",
      element: (
        <InfoIcon
          gap="8px"
          icon={<CalendarClock />}
          text={`Built ${yearBuilt ? yearBuilt : "--"}`}
        />
      ),
    },
    {
      key: "type",
      element: (
        <InfoIcon
          gap="8px"
          icon={<House size={16} />}
          text={`${type ? type : "--"} `}
        />
      ),
    },
  ];

  // Filter stats to show
  const statsToShow = show
    ? allStats.filter((stat) => show.includes(stat.key))
    : allStats;

  return (
    <div className="max-w-[90vw] min-h-[16px] flex gap-2 flex-wrap">
      {statsToShow.map((stat, idx) => (
        <React.Fragment key={`listing-stats-fragment-${stat.key}`}>
          <span key={`listing-stats-${stat.key}`}>
            {stat.element}
          </span>
          {idx < statsToShow.length - 1 && <Divider key={`listing-stats-divider-${stat.key}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};
